import {
   DashTimeRange,
   ChartFormat,
   ClientChartType,
   offsetDateByTime,
   linebarFormatExpand,
   linebarChartTransformerAnomalies,
   donutFormatExpand,
   DonutChartFormat,
   statsFormatExpand,
} from '@metrika/types';
import { PayloadAction } from '@reduxjs/toolkit';
import Axios from 'axios';
import { Task } from 'redux-saga';
import { put, call, takeEvery, fork, select, take, cancel, cancelled } from 'redux-saga/effects';
import { handleAPIFailure } from '../../../_shared/api';
import { getChart } from '../../api';
import type { RemoteChart, RemoteChartTablePaged } from '../../api/io';
import type { ChartStatePartial, ChartTypeResponder } from '../../charts/_types';
import { getResponderForType } from '../../charts/responders';
import type { DashboardFilter } from '../../hooks/dashFilters.types';
import {
   loadChartFailed,
   loadChartRequested,
   loadChartSuccess,
   setTimeRange,
   subscribeToChart,
   unsubscribeFromChart,
   unloadChart,
   setChartSearch,
   updateChart,
   setTableSort,
   downloadCsv,
} from '../reducers';
import { getChartsAndMaxSamples } from '../selectors';
import { downloadCsvSaga } from './downloadCsvSaga';
import { pollingService } from './polling';
import { updateChartSaga } from './updateChartSaga';

//todo rename id to viewId
function* handleLoadChart(
   action: PayloadAction<{ extraQuery?: string; id: string; dataId: string; maxSamples: number }>,
) {
   console.log(`API >> Fetching chart ${action.payload.id}`);
   const cancelTokenSource = Axios.CancelToken.source();
   try {
      const timeRange: { range: DashTimeRange; start?: string; end?: string } | undefined = yield select(
         (state) => state.charts.timeRange,
      );
      const timeEnd: string = yield select((state) => state.charts.timeEnd);
      const end =
         timeRange?.range === 'custom' && timeRange?.end ? new Date(timeRange.end) : new Date(timeEnd || Date.now());
      const start =
         timeRange?.range === 'custom' && timeRange?.start
            ? new Date(timeRange.start)
            : !!timeRange?.range && timeRange.range !== 'custom'
              ? offsetDateByTime(end, timeRange.range)
              : end;

      // Make api call with additional hints (remote id, time range, max samples)
      const requestId = action.payload.dataId;

      if (!requestId) throw new Error(`Chart dataId not found for chart : ${action.payload.id}`);

      const filters: DashboardFilter[] = yield select((s) => s.charts.filters);

      const resp: RemoteChart = yield call(getChart, {
         id: requestId,
         start,
         end,
         filters,
         maxSamples: action.payload.maxSamples,
         cancelToken: cancelTokenSource.token,
         extraQuery: action.payload.extraQuery,
      });

      const metadata = resp.metadata.views ? resp.metadata.views[action.payload.id.split('$')[0]] : undefined;

      if (!metadata) throw new Error(`Chart view not found: ${action.payload.id}`);

      if (metadata.type === 'linebar-time' && (resp.type === 'line' || resp.type === 'bar')) {
         // Expand the format to include the dynamic time series
         metadata.format = linebarFormatExpand(resp.data, metadata.format);
      }

      if (metadata.type === 'donut' && resp.type === 'stats') {
         // Expand the format to include the dynamic time series
         // @ts-ignore
         metadata.format = donutFormatExpand(resp.data, metadata.format as DonutChartFormat);
      }

      if (metadata.type === 'statistic' && resp.type === 'stats') {
         // Expand the format to include the dynamic stats ids
         // @ts-ignore
         metadata.format = statsFormatExpand(resp.data, metadata.format as StatisticChartFormat);
      }

      // Transform the chart data to our local format. See Responders for more detail.
      const responder: ChartTypeResponder<ClientChartType> = yield call(getResponderForType, metadata.type);
      // @ts-ignore todo(matt) not sure why ts is not ok with this expression. Need to work that out.
      const data: ReturnType<ChartTypeResponder<ClientChartType>['transform']> = yield call(
         responder.transform,
         resp.data,
         metadata.format,
      );
      const chart: ChartStatePartial = {
         filename: resp.type === 'table_paged' ? (resp as RemoteChartTablePaged).filename : undefined,
         upload_timestamp: resp.type === 'table_paged' ? (resp as RemoteChartTablePaged).upload_timestamp : undefined,
         id: action.payload.id,
         dataId: action.payload.dataId,
         updateMethod: resp.metadata.updateMethod,
         type: metadata.type,
         data,
         dataAnomalies:
            (resp.type === 'line' || resp.type === 'bar') && !!resp.data_anomalies
               ? linebarChartTransformerAnomalies(resp.data_anomalies)
               : undefined,
         interval: resp.metadata?.interval,
         paged:
            metadata.type === 'datatable' && resp.type === 'table_paged'
               ? {
                    page: resp.metadata.page,
                    cursor: resp.metadata.cursor,
                    nextPage: resp.metadata.nextPage,
                    totalPages: resp.metadata.totalPages,
                 }
               : undefined,
      } as ChartStatePartial; //todo remove when union tagged fixed

      // Load the static chart format

      const format: ChartFormat = metadata.format;

      // Store the chart in redux (this will replace the existing chart and throw out any partial data) and subscribe
      yield put(loadChartSuccess({ chart, format }));
      yield put(
         subscribeToChart({
            id: action.payload.id,
            updateMethod: chart.updateMethod,
            maxSamples: action.payload.maxSamples,
         }),
      );
   } catch (e: unknown) {
      yield call(handleAPIFailure, e, (errorMessage: string) =>
         loadChartFailed({ id: action.payload.id, errorMessage }),
      );
   } finally {
      const hasBeenCancelled: boolean = yield cancelled();
      if (hasBeenCancelled) {
         cancelTokenSource.cancel('cancelled from user');
         console.log(`API >> CANCELLED loading chart ${action.payload.id}`);
      }
   }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function* handleTimeChange(_: PayloadAction<number>) {
   // Reducer has updated the time field which will be used when fetching the chart again.

   const reloadData: { id: string; dataId: string; maxSamples: number; extraQuery?: string }[] =
      yield select(getChartsAndMaxSamples);

   for (const chart of reloadData) {
      yield put(loadChartRequested(chart));
      /* Unsubscribe here so that we do not get new data points after the new chart has arrived.
       * The worry is a case like the following:
       *    > GET /chart/{id}
       *    < GET /chart/{id}
       *    < WSS new_data {id} (bad, we now have a useless data point)
       *    > WSS resubscribe {id}
       */
      yield put(unsubscribeFromChart(chart.id));
   }
}

function* chartsLoadingService() {
   const chartsLoading: { [id: string]: Task } = {};

   function* cancelChartLoading(id: string, reloading?: boolean) {
      if (chartsLoading[id]?.isRunning()) {
         try {
            console.log(
               `API >> CANCEL REQUEST ${reloading ? 're-loading' : 'loading'} chart ${id} with SagaTaskId: `,
               // @ts-ignore
               chartsLoading[id].id,
            );
            yield cancel(chartsLoading[id]);
            delete chartsLoading[id];
         } catch (e) {
            console.error('cancelChartLoading.error', e);
         }
      }
   }

   function* handleUnloadChart(action: PayloadAction<string>) {
      yield call(cancelChartLoading, action.payload);
      yield call(cancelChartsUpdating, action.payload);
      // Data removed in reducer
      yield put(unsubscribeFromChart(action.payload));
   }

   yield takeEvery(unloadChart.toString(), handleUnloadChart);

   const chartsUpdatingTask: { [id: string]: Task } = {};

   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   function* cancelChartsUpdating(id: string, reloading?: boolean) {
      if (chartsUpdatingTask[id]?.isRunning()) {
         try {
            yield cancel(chartsUpdatingTask[id]);
            delete chartsUpdatingTask[id];
         } catch (e) {
            console.error('cancelChartsUpdating.error', e);
         }
      }
   }

   function* handleChartSearch(action: PayloadAction<{ search: string; chartIds: string[] }>) {
      for (const chartId of action.payload.chartIds) {
         yield put(updateChart({ viewId: chartId }));
      }
   }

   function* handleChartUpdate(action: PayloadAction<{ viewId: string; cursor?: 'back' | 'forward' }>) {
      const { viewId } = action.payload;
      yield call(cancelChartsUpdating, viewId);
      yield put(unsubscribeFromChart(viewId));
      chartsUpdatingTask[viewId] = yield fork(updateChartSaga, action);
   }

   yield takeEvery(setChartSearch.toString(), handleChartSearch);

   yield takeEvery(updateChart.toString(), handleChartUpdate);
   yield takeEvery(downloadCsv.toString(), downloadCsvSaga);

   function* handleTableSort(
      action: PayloadAction<{ viewId: string; sortCol: string; paginationType: 'client' | 'server' }>,
   ) {
      if (action.payload.paginationType === 'server') {
         yield put(updateChart({ viewId: action.payload.viewId }));
      }
   }
   yield takeEvery(setTableSort.toString(), handleTableSort);

   while (true) {
      const action: PayloadAction<{ id: string; dataId: string; maxSamples: number }> = yield take(
         loadChartRequested.toString(),
      );
      if (chartsLoading[action.payload.id]?.isRunning()) {
         console.log('API >> Chart already loading, skipping', action.payload.id);
         continue;
      }
      yield call(cancelChartLoading, action.payload.id, true);
      chartsLoading[action.payload.id] = yield fork(handleLoadChart, action);
   }
}

function* chartSaga() {
   yield fork(pollingService);

   // Handle cold start and updates
   yield fork(chartsLoadingService);
   yield takeEvery(setTimeRange.toString(), handleTimeChange);
}

export default chartSaga;
